/* eslint-disable react/no-danger */
import React from 'react';
import { graphql } from 'gatsby';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import Img from 'gatsby-image';
import { v4 } from 'uuid';

import Layout from '../components/layout';
import Container from '../components/Container';
import SEO from '../components/seo';
import {
  Hero,
  HeroHeading,
  SectionTitle,
  SmallSectionTitle,
  BodyText,
  ContentWrap
} from '../components/Global';

import CallToAction from '../components/CallToAction';
import MediaBreakDown from '../styles/MediaBreakDown';

const HeroWrap = styled(Hero)`
  .gatsby-image-wrapper {
    width: 105px;
    height: 105px;
    margin: auto;
    margin-bottom: 33px;
  }
  ${MediaBreakDown.md`
    .gatsby-image-wrapper {
      width: 80px;
      height: 80px;
    }
  `}
`;

const FeaturesWrap = styled.div`
  width: 100%;
  max-width: 1100px;
  margin: auto;
  .feature {
    display: flex;
    margin-bottom: 75px;
    align-items: center;
    justify-content: space-between;
    .feature-image {
      width: 35%;
      .gatsby-image-wrapper {
        width: 100%;
      }
    }
    .feature-text {
      width: 60%;
    }
  }
  .feature:nth-child(2n){
    flex-direction: row-reverse;
  }
  ${MediaBreakDown.lg`
    .feature {
      margin-bottom: 60px;
    }
  `}
  ${MediaBreakDown.md`
    .feature {
      flex-wrap: wrap;
      .feature-text {
        width: 100%;
        text-align: center;
      }
      .feature-image {
        width: 100%;
        max-width: 350px;
        margin: 0 auto;
      }
    }
  `}
`;

const LastSection = styled.div`
  margin: 0 auto 160px;
  width: 100%;
  max-width: 760px;
  text-align: center;

  p {
    max-width: 450px;
    margin: 12px auto 40px;
  }
`;

const ExtensionPage = ({ location, data }) => (
  <Layout location={location}>
    <SEO title="Workast Help" />
    <Container>
      <HeroWrap>
        <Img fluid={data.contentfulExtensionPage.icon.fluid} />
        <HeroHeading mb="21px">{data.contentfulExtensionPage.title}</HeroHeading>
        <BodyText mb="35px">{data.contentfulExtensionPage.shortDescription.shortDescription}</BodyText>
        <CallToAction text="Get started" />
      </HeroWrap>
      { data.contentfulExtensionPage.features ? (
        <FeaturesWrap>
          {
            data.contentfulExtensionPage.features.map(feature => (
              <div className="feature" key={v4()}>
                <div className="feature-text">
                  <SmallSectionTitle mb="20px">
                    {feature.title}
                  </SmallSectionTitle>
                  <ContentWrap mb="0">
                    <div
                      dangerouslySetInnerHTML={{
                        __html: feature.description.childContentfulRichText.html
                      }}
                    />
                  </ContentWrap>
                </div>
                <div className="feature-image">
                  <Img fluid={feature.image.fluid} />
                </div>
              </div>
            ))
          }
        </FeaturesWrap>
      ) : (
        <></>
      )}
      
      { data.contentfulExtensionPage.content ? (
        <ContentWrap>
          <div
            dangerouslySetInnerHTML={{
              __html: data.contentfulExtensionPage.content.childContentfulRichText.html
            }}
          />
        </ContentWrap>
      ) : (
        <></>
      )}
      <LastSection>
        <SectionTitle>
          Make teamwork a breeze
        </SectionTitle>
        <BodyText>
          Install Workast to your Slack workspace and start organizing your team work today.
        </BodyText>
        <CallToAction text="Get started" />
      </LastSection>
    </Container>
  </Layout>
);

ExtensionPage.propTypes = {
  location: PropTypes.shape({
    host: PropTypes.string,
    hostname: PropTypes.string,
    href: PropTypes.string,
    key: PropTypes.string,
    origin: PropTypes.string,
    pathname: PropTypes.string,
    port: PropTypes.string,
  }).isRequired,
  data: PropTypes.shape({
    contentfulExtensionPage: PropTypes.shape({
      name: PropTypes.string,
      title: PropTypes.string,
      slug: PropTypes.string,
      shortDescription: PropTypes.shape({
        shortDescription: PropTypes.string
      }).isRequired,
      // eslint-disable-next-line react/forbid-prop-types
      icon: PropTypes.object,
      // eslint-disable-next-line react/forbid-prop-types
      content: PropTypes.object,
      features: PropTypes.shape({
        map: PropTypes.shape({
          title: PropTypes.string,
          // eslint-disable-next-line react/forbid-prop-types
          description: PropTypes.object,
          // eslint-disable-next-line react/forbid-prop-types
          image: PropTypes.object,
        }).isRequired,
      })
    }).isRequired,
  }).isRequired,
};

export default ExtensionPage;

export const pageQuery = graphql`
  query ExtensionPageQuery($slug: String! ){
    contentfulExtensionPage(slug: { eq: $slug }) {
      name
      title
      slug
      shortDescription {
        shortDescription
      }
      icon {
        fluid {
          aspectRatio
          base64
          sizes
          src
          srcSet
          srcSetWebp
          srcWebp
        }
      }
      content {
        childContentfulRichText {
          html
        }
      }
      features {
        title
        description {
          childContentfulRichText {
            html
          }
        }
        image {
          fluid {
            aspectRatio
            base64
            sizes
            src
            srcSet
            srcSetWebp
            srcWebp
          }
        }
      }
    }
  }
`;
